import { useMutation, useMutationState, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { streamApiEvents } from "../../../shared/lib/api-client.ts";
import { queryKeys } from "../../../shared/lib/query-keys.ts";
import {
  appendMessageLog,
  applyEvPriorityAction,
  attachLinkedInIdentityToRelationship,
  bulkImportTwitterHandles,
  captureTelegramRelationship,
  captureTwitterRelationship,
  createEvGoogleCalendarEvent,
  createEvGoogleGmailDraft,
  createEvWorkflowEvent,
  discardXQueueDraft,
  disconnectEvGoogleConnection,
  disconnectTelegramMtproto,
  disconnectXConnection,
  generateXPromptExamples,
  getEvAgentContext,
  getEvBrief,
  getEvContacts,
  getEvFollowUps,
  getEvGoogleConnectionStatus,
  getEvOwnerOptions,
  getEvProjectCallPrep,
  getEvProjectCallPrepStatus,
  getEvSummary,
  getEvWorkstationProjectDetail,
  getEvWorkstationProjects,
  getTelegramMtprotoStatus,
  getXAuditTrail,
  getXConnectionStatus,
  getXOutreachPromptSettings,
  getXQueueDraft,
  getXThread,
  listXQueueContacts,
  listXQueueDrafts,
  prepareEvProjectCallReady,
  previewXMessage,
  previewXQueueDm,
  queueEvHandoff,
  recordTouchpoints,
  refreshEvGoogleConnection,
  regenerateEvProjectCallPrep,
  repairXQueueDraft,
  resolveXHandle,
  retryEvHandoff,
  saveXOutreachPromptSettings,
  sendEvGoogleGmail,
  sendTelegramMtprotoMessage,
  sendXMessage,
  sendXQueueDm,
  startEvGoogleConnect,
  startTelegramMtprotoAuth,
  startXConnect,
  testTelegramMtprotoCredentials,
  updateEvGoogleCalendarEvent,
  updateEvIntroContact,
  updateEvWorkflow,
  updateOutreachRelationship,
  updateOutreachRelationshipTarget,
  updateRelationshipFollowUp,
  verifyTelegramMtprotoAuth,
} from "../api/ev-workstation-api.ts";
import {
  applyOptimisticIntroContactPatch,
  applyOptimisticWorkflowPatch,
} from "../lib/optimistic-workflow.ts";
import type {
  AdminOwnerOption,
  EvAgentContext,
  EvBriefPayload,
  EvCallPrepArtifact,
  EvCallPrepStatus,
  EvCallReadyRequest,
  EvCaptureTelegramRelationshipRequest,
  EvCaptureTwitterRelationshipRequest,
  EvContactSearchParams,
  EvContactSearchResult,
  EvGoogleCalendarEventCreateRequest,
  EvGoogleCalendarEventPayload,
  EvGoogleCalendarEventUpdateRequest,
  EvGoogleConnectionStatusPayload,
  EvGoogleConnectStartPayload,
  EvGoogleGmailDraftPayload,
  EvGoogleGmailDraftRequest,
  EvGoogleGmailSendPayload,
  EvGoogleGmailSendRequest,
  EvGoogleRefreshPayload,
  EvIntroContactUpdateInput,
  EvLinkedInAttachRequest,
  EvMessageLogRequest,
  EvPriorityActionInput,
  EvProjectArtifact,
  EvRelationshipTargetUpdateRequest,
  EvRelationshipUpdateRequest,
  EvTelegramMtprotoStatusPayload,
  EvTelegramSendPayload,
  EvTelegramSendRequest,
  EvTouchpointRequest,
  EvWorkflowEventPayload,
  EvWorkflowPatch,
  EvWorkstationDetail,
  EvWorkstationFilters,
  EvWorkstationProject,
  EvWorkstationSummary,
  EvXAuditRecord,
  EvXConnectionStatusPayload,
  EvXDraft,
  EvXMessageRecord,
  EvXPreviewPayload,
  EvXPreviewRequest,
  EvXPromptSettingsPayload,
  EvXQueueContact,
  EvXResolveHandlePayload,
  EvXSendPayload,
  EvXSendRequest,
} from "../types/index.ts";

const EV_WORKFLOW_UPDATE_MUTATION_KEY = ["ev-workstation", "workflow-update"] as const;
const EV_INTRO_CONTACT_UPDATE_MUTATION_KEY = ["ev-workstation", "intro-contact-update"] as const;

function isActiveCallPrepStatus(
  status: EvCallPrepStatus["status"] | EvProjectArtifact["status"] | null | undefined
): boolean {
  return (
    status === "queued" ||
    status === "retrieving" ||
    status === "drafting" ||
    status === "validating"
  );
}

function isTerminalCallPrepStatus(status: EvCallPrepStatus["status"] | null | undefined): boolean {
  return (
    status === "idle" || status === "completed" || status === "needs_input" || status === "failed"
  );
}

function invalidateWorkstationQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId?: string
) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.projectsRoot() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.followUps() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.summary() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.brief() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.googleStatus() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.telegramStatus() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.xStatus() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.contactsRoot() });
  void queryClient.invalidateQueries({ queryKey: ["ev-workstation", "x-queue-drafts"] });

  if (projectId) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.project(projectId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.callPrep(projectId) });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.evWorkstation.callPrepStatus(projectId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.evWorkstation.agentContext(projectId),
    });
    void queryClient.invalidateQueries({ queryKey: ["project-detail", projectId] });
  }
}

export function useEvWorkstationProjectsQuery(enabled: boolean, filters: EvWorkstationFilters) {
  return useQuery<EvWorkstationProject[]>({
    queryKey: queryKeys.evWorkstation.projects(filters),
    queryFn: () => getEvWorkstationProjects(filters),
    enabled,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useEvWorkstationProjectDetailQuery(projectId: string | null, enabled: boolean) {
  return useQuery<EvWorkstationDetail | null>({
    queryKey: queryKeys.evWorkstation.project(projectId ?? ""),
    queryFn: () => getEvWorkstationProjectDetail(projectId ?? ""),
    enabled: enabled && Boolean(projectId),
  });
}

export function useEvAgentContextQuery(projectId: string | null, enabled: boolean) {
  return useQuery<EvAgentContext | null>({
    queryKey: queryKeys.evWorkstation.agentContext(projectId ?? ""),
    queryFn: () => getEvAgentContext(projectId ?? ""),
    enabled: enabled && Boolean(projectId),
  });
}

export function useEvFollowUpsQuery(enabled: boolean) {
  return useQuery<EvWorkstationProject[]>({
    queryKey: queryKeys.evWorkstation.followUps(),
    queryFn: () => getEvFollowUps(),
    enabled,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useEvSummaryQuery(enabled: boolean) {
  return useQuery<EvWorkstationSummary | null>({
    queryKey: queryKeys.evWorkstation.summary(),
    queryFn: () => getEvSummary(),
    enabled,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useEvBriefQuery(enabled: boolean) {
  return useQuery<EvBriefPayload | null>({
    queryKey: queryKeys.evWorkstation.brief(),
    queryFn: () => getEvBrief(),
    enabled,
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

export function useEvContactsQuery(enabled: boolean, params: EvContactSearchParams) {
  return useQuery<EvContactSearchResult[]>({
    queryKey: queryKeys.evWorkstation.contacts(params),
    queryFn: () => getEvContacts(params),
    enabled,
    staleTime: 30_000,
  });
}

export function useEvGoogleConnectionStatusQuery(enabled: boolean) {
  return useQuery<EvGoogleConnectionStatusPayload | null>({
    queryKey: queryKeys.evWorkstation.googleStatus(),
    queryFn: () => getEvGoogleConnectionStatus(),
    enabled,
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

export function useTelegramStatusQuery(enabled: boolean) {
  return useQuery<EvTelegramMtprotoStatusPayload | null>({
    queryKey: queryKeys.evWorkstation.telegramStatus(),
    queryFn: () => getTelegramMtprotoStatus(),
    enabled,
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

export function useXConnectionStatusQuery(enabled: boolean) {
  return useQuery<EvXConnectionStatusPayload | null>({
    queryKey: queryKeys.evWorkstation.xStatus(),
    queryFn: () => getXConnectionStatus(),
    enabled,
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

export function useXPromptSettingsQuery(enabled: boolean) {
  return useQuery<EvXPromptSettingsPayload | null>({
    queryKey: queryKeys.evWorkstation.xPromptSettings(),
    queryFn: () => getXOutreachPromptSettings(),
    enabled,
    staleTime: 60_000,
  });
}

export function useXThreadQuery(relationshipId: string | null, enabled: boolean) {
  return useQuery<EvXMessageRecord[]>({
    queryKey: queryKeys.evWorkstation.xThread(relationshipId ?? ""),
    queryFn: () => getXThread(relationshipId ?? ""),
    enabled: enabled && Boolean(relationshipId),
    staleTime: 15_000,
  });
}

export function useXAuditTrailQuery(relationshipId: string | null, enabled: boolean) {
  return useQuery<EvXAuditRecord[]>({
    queryKey: queryKeys.evWorkstation.xAudit(relationshipId ?? ""),
    queryFn: () => getXAuditTrail(relationshipId ?? ""),
    enabled: enabled && Boolean(relationshipId),
    staleTime: 15_000,
  });
}

export function useXQueueContactsQuery(
  enabled: boolean,
  params: { search?: string; limit?: number }
) {
  return useQuery<EvXQueueContact[]>({
    queryKey: queryKeys.evWorkstation.xQueueContacts(params),
    queryFn: () => listXQueueContacts(params),
    enabled,
    staleTime: 30_000,
  });
}

export function useXQueueDraftsQuery(enabled: boolean, params: { status?: string } = {}) {
  return useQuery<EvXDraft[]>({
    queryKey: queryKeys.evWorkstation.xQueueDrafts(params),
    queryFn: () => listXQueueDrafts(params),
    enabled,
    staleTime: 30_000,
  });
}

export function useXQueueDraftQuery(draftId: string | null, enabled: boolean) {
  return useQuery<EvXDraft | null>({
    queryKey: queryKeys.evWorkstation.xQueueDraft(draftId ?? ""),
    queryFn: () => getXQueueDraft(draftId ?? ""),
    enabled: enabled && Boolean(draftId),
  });
}

export function useEvProjectCallPrepQuery(projectId: string | null, enabled: boolean) {
  return useQuery<EvProjectArtifact<EvCallPrepArtifact> | null>({
    queryKey: queryKeys.evWorkstation.callPrep(projectId ?? ""),
    queryFn: () => getEvProjectCallPrep(projectId ?? ""),
    enabled: enabled && Boolean(projectId),
  });
}

export function useEvProjectCallPrepStatusQuery(projectId: string | null, enabled: boolean) {
  return useQuery<EvCallPrepStatus | null>({
    queryKey: queryKeys.evWorkstation.callPrepStatus(projectId ?? ""),
    queryFn: () => getEvProjectCallPrepStatus(projectId ?? ""),
    enabled: enabled && Boolean(projectId),
    refetchInterval: 30_000,
  });
}

export function useEvProjectCallPrepStatusStream(projectId: string | null, enabled: boolean) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || !projectId) return;

    const statusKey = queryKeys.evWorkstation.callPrepStatus(projectId);
    const abortController = new AbortController();
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;
    let terminalSeen = false;

    const clearReconnect = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const maybeScheduleReconnect = () => {
      if (disposed || terminalSeen) return;
      const current = queryClient.getQueryData<EvCallPrepStatus | null>(statusKey);
      if (!current || !isActiveCallPrepStatus(current.status)) return;
      clearReconnect();
      reconnectTimer = setTimeout(() => {
        void connect();
      }, 1_500);
    };

    const connect = async () => {
      if (disposed) return;

      try {
        await streamApiEvents<EvCallPrepStatus>(
          `/v1/ev-workstation/projects/${projectId}/call-prep/stream`,
          {
            signal: abortController.signal,
            onEvent: (event) => {
              if (event.event !== "status" && event.event !== "complete") {
                return;
              }

              const payload = event.data;
              if (!payload) return;

              queryClient.setQueryData(statusKey, payload);

              if (isTerminalCallPrepStatus(payload.status)) {
                terminalSeen = true;
                clearReconnect();
                invalidateWorkstationQueries(queryClient, projectId);
                return;
              }
            },
          }
        );
      } catch (error) {
        if (abortController.signal.aborted || disposed) {
          return;
        }

        const current = queryClient.getQueryData<EvCallPrepStatus | null>(statusKey);
        if (current && isActiveCallPrepStatus(current.status)) {
          queryClient.setQueryData(statusKey, {
            ...current,
            progressMessage:
              current.progressMessage ?? "Live connection interrupted. Reconnecting…",
          });
        } else {
          console.error("[ev-workstation] Call prep status stream failed", error);
        }
      } finally {
        maybeScheduleReconnect();
      }
    };

    void connect();

    return () => {
      disposed = true;
      clearReconnect();
      abortController.abort();
    };
  }, [enabled, projectId, queryClient]);
}

export function useEvOwnerOptionsQuery(enabled: boolean) {
  return useQuery<AdminOwnerOption[]>({
    queryKey: queryKeys.evWorkstation.owners(),
    queryFn: () => getEvOwnerOptions(),
    enabled,
    staleTime: 60_000,
  });
}

export function usePendingEvWorkflowProjectIds() {
  const pendingMutations = useMutationState({
    filters: {
      mutationKey: EV_WORKFLOW_UPDATE_MUTATION_KEY,
      status: "pending",
    },
    select: (mutation) =>
      mutation.state.variables as { projectId: string; patch: EvWorkflowPatch } | undefined,
  });

  return new Set(
    pendingMutations
      .map((variables) => variables?.projectId)
      .filter((projectId): projectId is string => Boolean(projectId))
  );
}

export function usePendingEvIntroContactProjectIds() {
  const pendingMutations = useMutationState({
    filters: {
      mutationKey: EV_INTRO_CONTACT_UPDATE_MUTATION_KEY,
      status: "pending",
    },
    select: (mutation) =>
      mutation.state.variables as
        | {
            projectId: string;
            input: EvIntroContactUpdateInput;
          }
        | undefined,
  });

  return new Set(
    pendingMutations
      .map((variables) => variables?.projectId)
      .filter((projectId): projectId is string => Boolean(projectId))
  );
}

export function useUpdateEvWorkflowMutation(options?: {
  onError?: (error: Error, variables: { projectId: string; patch: EvWorkflowPatch }) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: EV_WORKFLOW_UPDATE_MUTATION_KEY,
    mutationFn: ({ projectId, patch }: { projectId: string; patch: EvWorkflowPatch }) =>
      updateEvWorkflow(projectId, patch),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.evWorkstation.projectsRoot() });

      const previousItems = queryClient
        .getQueriesData<EvWorkstationProject[]>({
          queryKey: queryKeys.evWorkstation.projectsRoot(),
        })
        .map(([queryKey, items]) => ({
          queryKey,
          item: items?.find((item) => item.project.id === variables.projectId) ?? null,
        }))
        .filter((entry) => entry.item !== null);

      for (const [queryKey, items] of queryClient.getQueriesData<EvWorkstationProject[]>({
        queryKey: queryKeys.evWorkstation.projectsRoot(),
      })) {
        if (!items) continue;

        queryClient.setQueryData<EvWorkstationProject[]>(
          queryKey,
          items.map((item) =>
            item.project.id === variables.projectId
              ? applyOptimisticWorkflowPatch(item, variables.patch)
              : item
          )
        );
      }

      return { previousItems };
    },
    onError: (error, variables, context) => {
      for (const entry of context?.previousItems ?? []) {
        queryClient.setQueryData<EvWorkstationProject[]>(
          entry.queryKey,
          (items) =>
            items?.map((item) =>
              item.project.id === variables.projectId ? (entry.item ?? item) : item
            ) ?? items
        );
      }

      options?.onError?.(
        error instanceof Error ? error : new Error("Workflow update failed"),
        variables
      );
    },
    onSettled: (_, __, variables) => {
      invalidateWorkstationQueries(queryClient, variables.projectId);
    },
  });
}

export function useUpdateEvIntroContactMutation(options?: {
  onError?: (
    error: Error,
    variables: { projectId: string; input: EvIntroContactUpdateInput }
  ) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: EV_INTRO_CONTACT_UPDATE_MUTATION_KEY,
    mutationFn: ({ projectId, input }: { projectId: string; input: EvIntroContactUpdateInput }) =>
      updateEvIntroContact(projectId, input),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.evWorkstation.projectsRoot() });

      const previousItems = queryClient
        .getQueriesData<EvWorkstationProject[]>({
          queryKey: queryKeys.evWorkstation.projectsRoot(),
        })
        .map(([queryKey, items]) => ({
          queryKey,
          item: items?.find((item) => item.project.id === variables.projectId) ?? null,
        }))
        .filter((entry) => entry.item !== null);

      for (const [queryKey, items] of queryClient.getQueriesData<EvWorkstationProject[]>({
        queryKey: queryKeys.evWorkstation.projectsRoot(),
      })) {
        if (!items) continue;

        queryClient.setQueryData<EvWorkstationProject[]>(
          queryKey,
          items.map((item) =>
            item.project.id === variables.projectId
              ? applyOptimisticIntroContactPatch(item, variables.input)
              : item
          )
        );
      }

      return { previousItems };
    },
    onError: (error, variables, context) => {
      for (const entry of context?.previousItems ?? []) {
        queryClient.setQueryData<EvWorkstationProject[]>(
          entry.queryKey,
          (items) =>
            items?.map((item) =>
              item.project.id === variables.projectId ? (entry.item ?? item) : item
            ) ?? items
        );
      }

      options?.onError?.(
        error instanceof Error ? error : new Error("Intro contact update failed"),
        variables
      );
    },
    onSettled: (_, __, variables) => {
      invalidateWorkstationQueries(queryClient, variables.projectId);
    },
  });
}

export function useApplyEvPriorityActionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, input }: { projectId: string; input: EvPriorityActionInput }) =>
      applyEvPriorityAction(projectId, input),
    onSuccess: (_, variables) => {
      invalidateWorkstationQueries(queryClient, variables.projectId);
    },
  });
}

export function useCreateEvWorkflowEventMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, event }: { projectId: string; event: EvWorkflowEventPayload }) =>
      createEvWorkflowEvent(projectId, event),
    onSuccess: (_, variables) => {
      invalidateWorkstationQueries(queryClient, variables.projectId);
    },
  });
}

export function useCaptureTelegramRelationshipMutation() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, EvCaptureTelegramRelationshipRequest>({
    mutationFn: (request) => captureTelegramRelationship(request),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.contactsRoot() });
      if (variables.targetId) {
        invalidateWorkstationQueries(queryClient, variables.targetId);
      }
    },
  });
}

export function useCaptureTwitterRelationshipMutation() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, EvCaptureTwitterRelationshipRequest>({
    mutationFn: (request) => captureTwitterRelationship(request),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.contactsRoot() });
      if (variables.targetId) {
        invalidateWorkstationQueries(queryClient, variables.targetId);
      }
    },
  });
}

export function useUpdateOutreachRelationshipMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    Error,
    { relationshipId: string; body: EvRelationshipUpdateRequest; projectId?: string | null }
  >({
    mutationFn: ({ relationshipId, body }) => updateOutreachRelationship(relationshipId, body),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.contactsRoot() });
      invalidateWorkstationQueries(queryClient, variables.projectId ?? undefined);
    },
  });
}

export function useUpdateOutreachRelationshipTargetMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    Error,
    { relationshipId: string; body: EvRelationshipTargetUpdateRequest }
  >({
    mutationFn: ({ relationshipId, body }) =>
      updateOutreachRelationshipTarget(relationshipId, body),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.contactsRoot() });
      invalidateWorkstationQueries(queryClient, variables.body.targetId ?? undefined);
    },
  });
}

export function useAttachLinkedInIdentityMutation() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, { relationshipId: string; body: EvLinkedInAttachRequest }>({
    mutationFn: ({ relationshipId, body }) =>
      attachLinkedInIdentityToRelationship(relationshipId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.contactsRoot() });
      invalidateWorkstationQueries(queryClient);
    },
  });
}

export function useUpdateRelationshipFollowUpMutation() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, { relationshipId: string; followUpDate: string }>({
    mutationFn: (request) => updateRelationshipFollowUp(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.contactsRoot() });
      invalidateWorkstationQueries(queryClient);
    },
  });
}

export function useBulkImportTwitterHandlesMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    Error,
    {
      targetType: "project" | "equity_product" | "standalone";
      targetId?: string;
      targetLabel: string;
      handles: string[];
    }
  >({
    mutationFn: (request) => bulkImportTwitterHandles(request),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.contactsRoot() });
      invalidateWorkstationQueries(queryClient, variables.targetId);
    },
  });
}

export function useQueueEvHandoffMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId: string) => queueEvHandoff(projectId),
    onSuccess: (_, projectId) => {
      invalidateWorkstationQueries(queryClient, projectId);
    },
  });
}

export function useRetryEvHandoffMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ handoffId }: { handoffId: string; projectId: string }) =>
      retryEvHandoff(handoffId),
    onSuccess: (_, variables) => {
      invalidateWorkstationQueries(queryClient, variables.projectId);
    },
  });
}

export function useRegenerateEvCallPrepMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectId: string) => regenerateEvProjectCallPrep(projectId),
    onSuccess: (data, projectId) => {
      if (data) {
        queryClient.setQueryData(queryKeys.evWorkstation.callPrepStatus(projectId), data);
      }
      invalidateWorkstationQueries(queryClient, projectId);
    },
  });
}

export function usePrepareEvCallReadyMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, request }: { projectId: string; request: EvCallReadyRequest }) =>
      prepareEvProjectCallReady(projectId, request),
    onSuccess: (_, variables) => {
      invalidateWorkstationQueries(queryClient, variables.projectId);
    },
  });
}

export function useStartEvGoogleConnectMutation() {
  return useMutation<EvGoogleConnectStartPayload | null>({
    mutationFn: () => startEvGoogleConnect(),
  });
}

export function useDisconnectEvGoogleConnectionMutation() {
  const queryClient = useQueryClient();

  return useMutation<EvGoogleConnectionStatusPayload | null>({
    mutationFn: () => disconnectEvGoogleConnection(),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.evWorkstation.googleStatus(), data);
      invalidateWorkstationQueries(queryClient);
    },
  });
}

export function useRefreshEvGoogleConnectionMutation() {
  const queryClient = useQueryClient();

  return useMutation<EvGoogleRefreshPayload | null, Error, "gmail" | "calendar" | "all">({
    mutationFn: (scope) => refreshEvGoogleConnection(scope),
    onSuccess: () => {
      invalidateWorkstationQueries(queryClient);
    },
  });
}

export function useCreateEvGoogleGmailDraftMutation() {
  const queryClient = useQueryClient();

  return useMutation<EvGoogleGmailDraftPayload | null, Error, EvGoogleGmailDraftRequest>({
    mutationFn: (request) => createEvGoogleGmailDraft(request),
    onSuccess: () => {
      invalidateWorkstationQueries(queryClient);
    },
  });
}

export function useSendEvGoogleGmailMutation() {
  const queryClient = useQueryClient();

  return useMutation<EvGoogleGmailSendPayload | null, Error, EvGoogleGmailSendRequest>({
    mutationFn: (request) => sendEvGoogleGmail(request),
    onSuccess: () => {
      invalidateWorkstationQueries(queryClient);
    },
  });
}

export function useCreateEvGoogleCalendarEventMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    EvGoogleCalendarEventPayload | null,
    Error,
    EvGoogleCalendarEventCreateRequest
  >({
    mutationFn: (request) => createEvGoogleCalendarEvent(request),
    onSuccess: () => {
      invalidateWorkstationQueries(queryClient);
    },
  });
}

export function useTelegramSendMutation() {
  const queryClient = useQueryClient();

  return useMutation<EvTelegramSendPayload | null, Error, EvTelegramSendRequest>({
    mutationFn: (request) => sendTelegramMtprotoMessage(request),
    onSuccess: () => {
      invalidateWorkstationQueries(queryClient);
    },
  });
}

export function useTelegramAuthMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.evWorkstation.telegramStatus() });
  };

  return {
    testCredentials: useMutation<{ ok: true } | null, Error, { apiId: number; apiHash: string }>({
      mutationFn: (request) => testTelegramMtprotoCredentials(request),
      onSuccess: invalidate,
    }),
    startAuth: useMutation<
      EvTelegramMtprotoStatusPayload | null,
      Error,
      { apiId: number; apiHash: string; phoneNumber: string }
    >({
      mutationFn: (request) => startTelegramMtprotoAuth(request),
      onSuccess: invalidate,
    }),
    verifyAuth: useMutation<
      EvTelegramMtprotoStatusPayload | null,
      Error,
      { code: string; password?: string | null }
    >({
      mutationFn: (request) => verifyTelegramMtprotoAuth(request),
      onSuccess: invalidate,
    }),
    disconnect: useMutation<EvTelegramMtprotoStatusPayload | null, Error, void>({
      mutationFn: () => disconnectTelegramMtproto(),
      onSuccess: invalidate,
    }),
  };
}

export function useStartXConnectMutation() {
  return useMutation<{ authorizationUrl: string; requiredScopes: string[] } | null>({
    mutationFn: () => startXConnect(),
  });
}

export function useDisconnectXMutation() {
  const queryClient = useQueryClient();

  return useMutation<EvXConnectionStatusPayload | null>({
    mutationFn: () => disconnectXConnection(),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.evWorkstation.xStatus(), data);
      invalidateWorkstationQueries(queryClient);
    },
  });
}

export function useXPreviewMessageMutation() {
  const queryClient = useQueryClient();

  return useMutation<EvXPreviewPayload | null, Error, EvXPreviewRequest>({
    mutationFn: (request) => previewXMessage(request),
    onSuccess: (data) => {
      if (data?.relationshipId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.evWorkstation.xThread(data.relationshipId),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.evWorkstation.xAudit(data.relationshipId),
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["ev-workstation", "x-queue-drafts"] });
    },
  });
}

export function useXSendMessageMutation() {
  const queryClient = useQueryClient();

  return useMutation<EvXSendPayload | null, Error, EvXSendRequest>({
    mutationFn: (request) => sendXMessage(request),
    onSuccess: (data) => {
      invalidateWorkstationQueries(queryClient, data?.projectId ?? undefined);
      if (data?.relationshipId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.evWorkstation.xThread(data.relationshipId),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.evWorkstation.xAudit(data.relationshipId),
        });
      }
    },
  });
}

export function useXQueuePreviewMutation() {
  const queryClient = useQueryClient();

  return useMutation<EvXPreviewPayload | null, Error, EvXPreviewRequest>({
    mutationFn: (request) => previewXQueueDm(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ev-workstation", "x-queue-drafts"] });
    },
  });
}

export function useXQueueSendMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    EvXSendPayload | null,
    Error,
    {
      draftId?: string | null;
      relationshipId?: string | null;
      body?: string | null;
      sendSource?: string;
    }
  >({
    mutationFn: (request) => sendXQueueDm(request),
    onSuccess: (data) => {
      invalidateWorkstationQueries(queryClient, data?.projectId ?? undefined);
      void queryClient.invalidateQueries({ queryKey: ["ev-workstation", "x-queue-drafts"] });
    },
  });
}

export function useSaveXPromptSettingsMutation() {
  const queryClient = useQueryClient();

  return useMutation<EvXPromptSettingsPayload | null, Error, string>({
    mutationFn: (template) => saveXOutreachPromptSettings(template),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.evWorkstation.xPromptSettings(), data);
    },
  });
}

export function useGenerateXPromptExamplesMutation() {
  return useMutation<
    { examples: Array<{ projectId: string; prompt: string }> } | null,
    Error,
    string[]
  >({
    mutationFn: (projectIds) => generateXPromptExamples(projectIds),
  });
}

export function useXResolveHandleMutation() {
  return useMutation<EvXResolveHandlePayload | null, Error, string>({
    mutationFn: (handle) => resolveXHandle(handle),
  });
}

export function useRecordTouchpointsMutation() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, EvTouchpointRequest | EvTouchpointRequest[]>({
    mutationFn: (request) => recordTouchpoints(request),
    onSuccess: () => invalidateWorkstationQueries(queryClient),
  });
}

export function useAppendMessageLogMutation() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, EvMessageLogRequest>({
    mutationFn: (request) => appendMessageLog(request),
    onSuccess: () => invalidateWorkstationQueries(queryClient),
  });
}

export function useDiscardXQueueDraftMutation() {
  const queryClient = useQueryClient();

  return useMutation<EvXDraft | null, Error, string>({
    mutationFn: (draftId) => discardXQueueDraft(draftId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ev-workstation", "x-queue-drafts"] });
    },
  });
}

export function useRepairXQueueDraftMutation() {
  const queryClient = useQueryClient();

  return useMutation<EvXDraft | null, Error, string>({
    mutationFn: (draftId) => repairXQueueDraft(draftId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ev-workstation", "x-queue-drafts"] });
    },
  });
}

export function useUpdateEvGoogleCalendarEventMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    EvGoogleCalendarEventPayload | null,
    Error,
    { eventId: string; request: EvGoogleCalendarEventUpdateRequest }
  >({
    mutationFn: ({ eventId, request }) => updateEvGoogleCalendarEvent(eventId, request),
    onSuccess: () => {
      invalidateWorkstationQueries(queryClient);
    },
  });
}
